/**
 * IconButton — Botón de solo icono con aria-label obligatorio por tipos.
 *
 * Centraliza el patrón icon-only para garantizar accesibilidad: todo IconButton
 * exige `aria-label` en tiempo de compilación.
 */
import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react'

type Variant = 'ghost' | 'subtle' | 'danger' | 'primary'
type Size = 'sm' | 'md' | 'lg'

interface IconButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'aria-label'> {
  'aria-label': string
  icon: ReactNode
  variant?: Variant
  size?: Size
}

const variantCls: Record<Variant, string> = {
  ghost: 'text-content-subtle hover:text-content-muted hover:bg-soft',
  subtle: 'text-content-muted hover:text-content hover:bg-soft',
  // El ícono de peligro del producto: `danger-solid` (500 en claro, 400 en
  // oscuro). Es el mismo que usan los botones de borrar escritos a mano desde el
  // 2026-09-30; el rojo 400 que tenían daba 2,77:1 sobre blanco, bajo el 3:1 de un ícono.
  danger: 'text-danger-solid hover:text-danger-content hover:bg-danger-surface',
  primary: 'text-primary hover:bg-primary/10',
}

const sizeCls: Record<Size, string> = {
  sm: 'p-1 rounded-md',
  md: 'p-1.5 rounded-md',
  lg: 'p-2 rounded-lg',
}

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(
  ({ icon, variant = 'ghost', size = 'md', className = '', type = 'button', ...rest }, ref) => {
    return (
      <button
        ref={ref}
        type={type}
        className={`inline-flex items-center justify-center transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer ${sizeCls[size]} ${variantCls[variant]} ${className}`}
        {...rest}
      >
        {icon}
      </button>
    )
  }
)

IconButton.displayName = 'IconButton'
